import React, { ReactNode } from "react"
import {
  Box, Flex, VStack, Button, Divider,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"

type MenuOptionItem = {
  label: string
  icon?: string
  onClick?: () => void
}

type MenuOptionDivider = {
  type: "divider"
}

type MenuOption = MenuOptionItem | MenuOptionDivider

type Props = {
  children: ReactNode
  menuTitle?: string
  menuIcon?: string
  menuOptions?: MenuOption[]
  noPadding?: boolean
  isLast?: boolean
  /** Use overflow hidden (e.g. for Map so it respects rounded corners) */
  overflowHidden?: boolean
  /** Top margin for the menu */
  menuMt?: number
  /** Minimum height for the section */
  minH?: number
}

const WebsitePreviewSection = ({
  children,
  menuTitle,
  menuIcon,
  menuOptions,
  noPadding = false,
  isLast = false,
  overflowHidden = false,
  menuMt,
  minH,
}: Props) => {
  const menuItems = menuOptions?.map((option, index) => {
    if ("type" in option && option.type === "divider") {
      return <Divider key={`divider-${index}`} borderColor="gray.100" opacity={0.8} />
    }

    const item = option as MenuOptionItem

    return (
      <Button
        key={`${item.label}-${item.icon || ""}-${index}`}
        variant="ghost"
        size="sm"
        fontSize="sm"
        w="100%"
        justifyContent="flex-start"
        onClick={item.onClick}
        color="gray.700"
        fontWeight="semibold"
        py={6}
        px={4}
        borderRadius="md"
        _hover={{
          bg: "gray.50",
        }}
        _active={{
          bg: "gray.100",
        }}
        transition="all 0.15s ease"
      >
        {item.label}
      </Button>
    )
  })

  return (
    <Flex position="relative" align="flex-start" justifyContent="space-between" w="100%">
      <Box
        w="80%"
        maxW="1140px"
        alignSelf="stretch"
        px={noPadding ? 0 : 3}
        py={noPadding ? 0 : 7}
        bgColor="white"
        borderLeftWidth={1}
        borderRightWidth={1}
        borderBottomWidth={isLast ? 1 : 0}
        borderColor="gray.300"
        borderBottomLeftRadius={isLast ? "lg" : 0}
        borderBottomRightRadius={isLast ? "lg" : 0}
        overflow={overflowHidden ? "hidden" : "visible"}
        minH={minH ?? 8}
      >
        {children}
      </Box>
      {(menuTitle && menuItems && menuItems.length > 0) && (
        <Box
          w={40}
          flexShrink={0}
          position="sticky"
          top={4}
          alignSelf="flex-start"
          mb={4}
          {...(menuMt != null && { mt: menuMt })}
        >
          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            bgColor="white"
            overflow="hidden"
            boxShadow="0 1px 3px rgba(0, 0, 0, 0.06)"
          >
            <Flex align="center" gap={3} px={4} py={3} bgColor="gray.50" borderBottomWidth={1} borderColor="gray.100">
              {menuIcon && <Icon icon={menuIcon} size={5} color="gray.600" />}
              <Box fontWeight="600" fontSize="md" color="gray.800" letterSpacing="tight">
                {menuTitle}
              </Box>
            </Flex>
            <VStack align="stretch" spacing={0}>
              {menuItems}
            </VStack>
          </Box>
        </Box>
      )}
    </Flex>
  )
}

export default WebsitePreviewSection
